import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpStatusCodes } from 'app/shared/http-status-codes';
import { UiConflictError } from 'app/shared/ui-conflict-error';
import { UiError } from 'app/shared/ui-error';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

const CONNECTION_RETRY_DELAY = 10000;
const A20_DOWNLOAD_URL = '/api/workflows/wfsvc/v0/ko/A20Download/StartWorkflow';

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptorService implements HttpInterceptor {
  private connectionFailure = false;

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      // Got to delay somewhere. If on error then they'll be delays showing the error which will make
      // it seem slow. Doing it this way at least avoids network spam.
      catchError((error: HttpErrorResponse) => {
        console.error('HTTP Error', error);

        const errorBody = error.error;

        const delayedThrow = (err: Error): Observable<HttpEvent<any>> => {
          if (this.connectionFailure) {
            return timer(CONNECTION_RETRY_DELAY).pipe(switchMap(() => throwError(err)));
          } else {
            return throwError(err);
          }
        };

        switch (error.status) {
          // Timeout, refused etc.
          case HttpStatusCodes.ConnectionError: {
            const res = delayedThrow(
              new UiError(
                'Failed to establish a connection to the server. Please check your connection',
                error.status,
              ),
            );
            if (!this.connectionFailure) {
              console.debug('Marking connection lost');
              this.connectionFailure = true;
            }
            return res;
          }
          // Bad request
          case HttpStatusCodes.BadRequest:
            // Directly renderable for the most part except for when
            // thrown by the JSON schema validator. However, all of those
            // sorts of errors should be pre-caught client side.
            return delayedThrow(new UiError(errorBody.message, error.status));
          // Unauthorized
          case HttpStatusCodes.Unauthorized:
            return delayedThrow(
              new UiError(
                'You do not have the correct permissions to access this page',
                error.status,
              ),
            );
          // Not found
          case HttpStatusCodes.NotFound:
            return delayedThrow(
              new UiError("The resource you're looking for cannot be found", error.status),
            );
          // Conflict
          case HttpStatusCodes.Conflict:
            if (errorBody?.errorObject) {
              return delayedThrow(new UiConflictError(errorBody.errorObject));
            } else {
              return delayedThrow(
                new UiError('One or more fields conflict on the server', error.status),
              );
            }
          // Generic server-side error
          case HttpStatusCodes.Unhandled:
            return delayedThrow(
              new UiError(
                // If the error is from A20 artifact download
                // then display the message returned by workflows
                error.url?.endsWith(A20_DOWNLOAD_URL) && error.error?.error
                  ? error.error.error
                  : 'An unexpected error occurred server-side. Please check the debug console for more information',
                error.status,
              ),
            );
          // Gateway timeout
          case HttpStatusCodes.BadGateway:
          case HttpStatusCodes.GatewayTimeout: {
            const res = delayedThrow(
              new UiError('The server is currently unavailable', error.status),
            );
            if (!this.connectionFailure) {
              console.debug('Marking connection lost');
              this.connectionFailure = true;
            }
            return res;
          }
          default:
            return delayedThrow(
              new UiError(
                'An unexpected connection error has occurred. Please check the debug console for more information',
                error.status,
              ),
            );
        }
      }),
      tap((event) => {
        if (this.connectionFailure && event instanceof HttpResponse) {
          console.debug('Connection restored');
          this.connectionFailure = false;
        }
      }),
    );
  }
}
